import React from "react";
import { Link } from "react-router-dom";
import Reveal from "../components/Reveal";
import PropertyCard from "../components/PropertyCard";
import GoldDivider from "../components/GoldDivider";
import { properties, formatPrice } from "../data/properties";
import { ArrowRight, MapPin } from "lucide-react";

const HERO =
    "https://images.pexels.com/photos/3195642/pexels-photo-3195642.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1800";

const Residential = () => {
    const list = properties.filter((p) => p.category === "residential");
    const spotlight = list[2];
    const rest = list.filter((p) => p.id !== spotlight.id);

    return (
        <div data-testid="page-residential">
            <section className="relative pt-40 pb-28 overflow-hidden">
                <div
                    className="absolute inset-0 opacity-60"
                    style={{ backgroundImage: `url(${HERO})`, backgroundSize: "cover", backgroundPosition: "center" }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-brand-jet" />
                <div className="relative max-w-5xl mx-auto px-6 text-center">
                    <Reveal>
                        <p className="text-[0.7rem] tracking-[0.55em] uppercase text-brand-gold mb-6">
                            Multi-Million-Dollar Estates
                        </p>
                        <h1 className="font-serif-editorial text-5xl md:text-6xl lg:text-7xl text-brand-silver leading-[1.05]">
                            A collection for the <span className="italic text-brand-champagne">very few.</span>
                        </h1>
                        <p className="mt-10 text-brand-silver/75 text-lg max-w-2xl mx-auto leading-relaxed">
                            The finest residential addresses in Summerlin, The Ridges, MacDonald Highlands,
                            Ascaya, and the Las Vegas Strip.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="py-24 md:py-32">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 grid md:grid-cols-12 gap-12 items-center">
                    <Reveal className="md:col-span-7 order-2 md:order-1">
                        <div className="overflow-hidden border border-brand-gold/20">
                            <img src={spotlight.image} alt={spotlight.name} className="w-full h-full object-cover" />
                        </div>
                    </Reveal>
                    <Reveal delay={150} className="md:col-span-5 order-1 md:order-2">
                        <p className="text-[0.7rem] tracking-[0.45em] uppercase text-brand-gold mb-4">{spotlight.tag}</p>
                        <h2 className="font-serif-editorial text-4xl md:text-5xl text-brand-champagne leading-tight">
                            {spotlight.name}
                        </h2>
                        <p className="mt-3 text-brand-silver/70 flex items-center gap-2 text-sm">
                            <MapPin size={14} className="text-brand-gold" /> {spotlight.location}
                        </p>
                        <p className="mt-8 text-brand-silver/75 leading-relaxed">
                            {spotlight.description}
                        </p>
                        <div className="mt-10 grid grid-cols-4 gap-4 text-center">
                            {[
                                ["Price", formatPrice(spotlight.price)],
                                ["Beds", spotlight.beds],
                                ["Baths", spotlight.baths],
                                ["SqFt", spotlight.sqft.toLocaleString()],
                            ].map(([k, v]) => (
                                <div key={k} className="border border-brand-gold/20 py-4">
                                    <div className="font-serif-editorial text-2xl text-brand-silver">{v}</div>
                                    <div className="mt-1 text-[0.6rem] tracking-[0.3em] uppercase text-brand-silver/50">{k}</div>
                                </div>
                            ))}
                        </div>
                        <Link
                            to="/contact"
                            className="mt-10 inline-flex items-center gap-2 text-xs tracking-[0.35em] uppercase border border-brand-gold text-brand-gold px-8 py-4 hover:bg-brand-gold hover:text-brand-jet transition-all"
                            data-testid="residential-spotlight-inquire"
                        >
                            Private Showing <ArrowRight size={14} />
                        </Link>
                    </Reveal>
                </div>
            </section>

            <GoldDivider />

            <section className="py-24">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <Reveal>
                        <h2 className="font-serif-editorial text-3xl md:text-4xl text-brand-silver mb-12">
                            The Collection
                        </h2>
                    </Reveal>
                    <div className="grid md:grid-cols-12 gap-8">
                        {rest.map((p, i) => (
                            <Reveal key={p.id} delay={i * 90} className="md:col-span-6 lg:col-span-4">
                                <PropertyCard p={p} />
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Residential;
